

import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { IoMdClose } from "react-icons/io";
import { useSelector } from "react-redux";
import Toolbar from "../Apps/Toolbar";
import { Placeholder } from "../Apps/Placeholder";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

type Message = {
    id: number;
    name: string;
    content?: string;
    time?: number
    event?: string
}
type cashierMssage = {
    toggleCashierMessageModal: () => void
    cashierModalOpen: boolean

}
export default function WhatsappMessage({ toggleCashierMessageModal, cashierModalOpen }: cashierMssage) {
    const { t } = useTranslation();

    const [cashierMessage, setCashierMessage] = useState<Message[]>([]);
    const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
    const baseUrl = import.meta.env.VITE_BASE_URL;
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);

    const whatsappEditor = useEditor({
        extensions: [
            StarterKit,
            Heading.configure({ levels: [1, 2, 3] }),
            Bold,
            BulletList,
            OrderedList,
            ListItem,
            Highlight.configure({ multicolor: true }),
            Placeholder,  // Placeholder extension for managing placeholders
        ],
        content: selectedMessage?.content || "",
        onUpdate: ({ editor }) => {
            if (!selectedMessage || !cashierMessage) return;

            // Tiptap handles the placeholders automatically now.
            const updatedMessages = cashierMessage.map((msg) =>
                msg.id === selectedMessage.id
                    ? { ...msg, content: editor.getHTML() }
                    : msg
            );

            setCashierMessage(updatedMessages);
        },
    });








    useEffect(() => {
        if (!whatsappEditor) return;

        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Check if the clicked element is a <mark> tag with the data-placeholder attribute
            if (target.nodeName === "MARK" && target.dataset.placeholder) {
                // Remove the mark (and its content) from the editor
                const updatedContent = whatsappEditor.getHTML().replace(target.outerHTML, "");

                // Set the updated content back to the editor
                whatsappEditor.commands.setContent(updatedContent, false);
            }
        };

        // Attach the click event listener to the editor DOM
        whatsappEditor.view.dom.addEventListener("click", handleClick);

        // Clean up the event listener when the component is unmounted
        return () => {
            whatsappEditor.view.dom.removeEventListener("click", handleClick);
        };
    }, [whatsappEditor]);

    useEffect(() => {
        if (whatsappEditor && selectedMessage && typeof selectedMessage.content === "string") {
            whatsappEditor.commands.setContent(selectedMessage.content, false);
        }
    }, [selectedMessage, whatsappEditor]);

    async function saveWhatsappMessage() {
        try {
            const options = {
                url: `${baseUrl}/api/dashboard/whatsapp-notifier/cashier?locale=${i18n.language}`,
                method: "POST",
                headers: {
                    Authorization: token,
                }, data: {
                    messages: cashierMessage
                }
            };
            const response = await axios(options);
            if (response.data.success === true) {
                toast.success(t("whatsappMess.saveSuccess") as string);
                toggleCashierMessageModal()

            } else {
                toast.error(t("whatsappMess.errorGeneric") as string);
            }
        } catch (error) {
            console.error(error);
        }
    }
    async function getWhatsappMessage() {
        try {
            const options = {
                url: `${baseUrl}/api/dashboard/whatsapp-notifier/cashier?locale=${i18n.language}`,
                method: "GET",
                headers: {
                    Authorization: token,
                },
            };
            const response = await axios(options);
            if (response.data.success === true) {
                const firstMessage = response.data.messages[0];
                setCashierMessage(response.data.messages);
                setSelectedMessage(firstMessage);

                setTimeout(() => {
                    if (whatsappEditor && firstMessage?.content) {
                        whatsappEditor.commands.setContent(firstMessage.content, false);
                    }
                }, 0);
            }
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (cashierModalOpen) {
            getWhatsappMessage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cashierModalOpen]);

    return (
        <div className="fixed inset-0 z-[99999] px-5 flex justify-center bg-black/50 ">
            <div className="flex  max-h-[90vh] min-h-[50vh] mt-10 relative lg:max-w-[50%] md:max-w-[70%] flex-col gap-3 rounded-lg bg-white px-4 py-3 pb-5 shadow-lg sm:px-5 sm:py-5 ">
                <IoMdClose size={24} className="text-red-600 absolute top-2 end-2 cursor-pointer" onClick={toggleCashierMessageModal} />
                <div className="text-center text-xl font-bold">{t("whatsappMess.cashierMessage")}</div>
            
                <div className="bg-white relative w-fit max-h-[90%] overflow-y-auto rounded-md">


                    <div className="w-full rounded-md flex flex-col gap-3 p-5 h-fit border">
                        <div className="flex flex-wrap gap-5">

                            <div className="flex flex-col gap-2">
                                <label>{t("whatsappMess.chooseMessageTime")}</label>
                                <div className="rounded-xl overflow-hidden border w-fit">
                                    <select
                                        value={selectedMessage?.time}
                                        onChange={(e) => {
                                            if (!selectedMessage || !cashierMessage) return;
                                            selectedMessage.time = parseInt(e.target.value);
                                            setCashierMessage([...cashierMessage]);
                                        }}
                                        className="px-5 py-1"
                                    >
                                        <option value="999">{t("Choose")}</option>
                                        <option value="0">{t("Instantly")}</option>
                                        <option value="1">{t("after1Minute")}</option>
                                        <option value="5">{t("after5Minutes")}</option>
                                        <option value="10">{t("after10Minutes")}</option>
                                        <option value="15">{t("after15Minutes")}</option>
                                        <option value="30">{t("after30Minutes")}</option>
                                        <option value="60">{t("after60Minutes")}</option>

                                    </select>
                                </div>
                            </div>
                        </div>
                        <Toolbar editor={whatsappEditor} />
                        <div className="h-[250px] overflow-auto">
                            <EditorContent editor={whatsappEditor} className="tiptap-editor" />
                        </div>
                    </div>
                </div>
                <button onClick={saveWhatsappMessage} className="my-btn">{t("whatsappMess.save")}</button>
            </div>
        </div >
    );
}
